<script setup>
import { computed, ref } from "vue";
import Vapor from "laravel-vapor";
import BrandButton from "@/Components/Brand/BrandButton.vue";

const props = defineProps({
    promos: {
        type: Object,
        required: true,
    },
});

function asset(path) {
    return Vapor.asset(path);
}

// Mobile: main + feature tiles become a snap carousel with pagination dots.
// lg: the main tile goes full width with the feature tiles 2-up beneath it
// (BH-29); xl+: main tile + a stacked feature column on the right.
const scroller = ref(null);
const activeSlide = ref(0);
const slideCount = computed(() => 1 + (props.promos.features?.length ?? 0));

function onScroll() {
    const el = scroller.value;

    if (!el) {
        return;
    }

    // Slides are full-width with a 16px (gap-4) gutter between them.
    activeSlide.value = Math.round(el.scrollLeft / (el.clientWidth + 16));
}

function goToSlide(index) {
    const el = scroller.value;

    el?.querySelectorAll("[data-slide]")[index]?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
    });
}
</script>

<template>
    <section>
        <div
            ref="scroller"
            class="flex gap-4 max-lg:-mx-4 max-lg:snap-x max-lg:snap-mandatory max-lg:overflow-x-auto max-lg:[-ms-overflow-style:none] max-lg:[scrollbar-width:none] sm:max-lg:-mx-7 lg:flex-col xl:flex-row max-lg:[&::-webkit-scrollbar]:hidden"
            @scroll="onScroll"
        >
            <!-- Main hero tile: live copy + CTA over a cut-out product photo.
                 The whole tile is the link when an expert list is chosen
                 (BH-60); the CTA is then a visual span so no control nests
                 inside the anchor. -->
            <component
                :is="promos.main.cta?.url ? 'a' : 'div'"
                :href="promos.main.cta?.url || undefined"
                data-slide
                class="relative isolate flex min-h-[320px] w-full shrink-0 snap-center overflow-hidden bg-brand-red no-underline [container-type:inline-size] sm:min-h-[360px] lg:min-h-[402px] lg:rounded-brand xl:w-auto xl:flex-1"
            >
                <!-- Tonal watermark behind the copy -->
                <span
                    v-if="promos.main.watermark"
                    aria-hidden="true"
                    class="pointer-events-none absolute bottom-[60%] left-[2%] z-0 select-none whitespace-nowrap font-brand-display text-[min(24cqw,170px)] font-black uppercase italic leading-[0.8] tracking-[-0.02em] text-[#dd092c]"
                >
                    {{ promos.main.watermark }}
                </span>

                <!-- Product imagery, anchored bottom-right -->
                <img
                    v-if="promos.main.image"
                    :src="asset(promos.main.image)"
                    :alt="promos.main.alt"
                    class="pointer-events-none absolute bottom-0 right-0 z-10 h-full w-auto max-w-[55%] object-contain object-right-bottom"
                />

                <!-- Copy + CTA -->
                <div
                    class="relative z-20 flex max-w-[62%] flex-col justify-center gap-3 p-6 sm:p-8 lg:py-10 lg:pl-[60px]"
                >
                    <p
                        v-if="promos.main.eyebrow"
                        class="font-brand-display text-[clamp(1rem,1.4vw,19px)] font-bold uppercase italic tracking-[-0.01em] text-[#efeee8]"
                    >
                        {{ promos.main.eyebrow }}
                    </p>

                    <h3
                        class="font-brand-display text-[clamp(2.25rem,4.5vw,60px)] font-black uppercase italic leading-[0.9] tracking-[-0.02em] text-white"
                    >
                        {{ promos.main.title }}
                    </h3>

                    <p
                        v-if="promos.main.description"
                        class="max-w-[402px] font-brand text-[15px] font-medium leading-[1.35] text-white"
                    >
                        {{ promos.main.description }}
                    </p>

                    <div v-if="promos.main.cta" class="mt-2">
                        <BrandButton
                            :as="promos.main.cta.url ? 'span' : null"
                            variant="whiteOnRed"
                            class="h-[44px] px-7 text-[15px]"
                        >
                            {{ promos.main.cta.label }}
                        </BrandButton>
                    </div>
                </div>
            </component>

            <!-- Feature tiles: own slides on mobile, 2-up row on lg, stacked
                 column on xl.
                 Live copy over an art-only collage (objects right, transparent
                 left) so nothing crops away on mobile (GH-1076). -->
            <div
                class="contents lg:flex lg:gap-4 xl:w-[321px] xl:shrink-0 xl:flex-col"
            >
                <component
                    :is="feature.url ? 'a' : 'div'"
                    v-for="feature in promos.features"
                    :key="feature.title ?? feature.image"
                    :href="feature.url || undefined"
                    data-slide
                    class="relative flex w-full shrink-0 snap-center flex-col justify-center overflow-hidden lg:min-h-[190px] lg:w-auto lg:flex-1 lg:rounded-brand"
                    :style="{ backgroundColor: feature.bg }"
                >
                    <img
                        v-if="feature.image"
                        :src="asset(feature.image)"
                        :alt="feature.alt ?? ''"
                        class="pointer-events-none absolute inset-0 size-full object-cover object-right"
                    />
                    <div class="relative max-w-[70%] p-6 lg:p-[31px] lg:pr-0">
                        <h3
                            class="font-brand-display text-[36px] font-black uppercase italic leading-[0.9] text-white lg:text-[33px]"
                        >
                            {{ feature.title }}
                        </h3>
                        <p
                            v-if="feature.description"
                            class="mt-3 font-brand text-[15px] font-medium leading-[1.35] text-white lg:mt-4"
                        >
                            {{ feature.description }}
                        </p>
                    </div>
                </component>
            </div>
        </div>

        <!-- Pagination markers (mobile only): 10px rounded squares per
             Figma 224-300 (GH-1069). -->
        <div class="mt-4 flex justify-center gap-[9px] lg:hidden">
            <button
                v-for="i in slideCount"
                :key="i"
                type="button"
                class="size-[10px] rounded-[2px] transition-colors"
                :class="
                    activeSlide === i - 1 ? '!bg-brand-red' : '!bg-[#ebebeb]'
                "
                :aria-label="`Go to slide ${i}`"
                @click="goToSlide(i - 1)"
            ></button>
        </div>
    </section>
</template>
